<template>
  <div class="prt" :class="{ 'prt--compact': compact }" role="region" :aria-label="ariaLabel">
    <header v-if="!compact" class="prt__header">
      <div class="prt__titles">
        <h2 class="prt__title">Pay range timeline</h2>
      </div>
      <div class="prt__controls">
        <div class="prt__btn-group" role="group" aria-label="Timeline window navigation">
          <button type="button" class="prt__btn" @click="shiftWindow('back')">‹</button>
          <button type="button" class="prt__btn" @click="recenterToday">Today</button>
          <button type="button" class="prt__btn" @click="shiftWindow('forward')">›</button>
        </div>
        <div class="prt__btn-group" role="group" aria-label="Timeline scale">
          <button
            v-for="option in SCALE_OPTIONS"
            :key="option.value"
            type="button"
            class="prt__btn"
            :class="{ 'prt__btn--active': scale === option.value }"
            :aria-pressed="String(scale === option.value)"
            @click="setScale(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </header>

    <div ref="scrollEl" class="prt__scroll">
    <div v-if="!loading && !compact" class="prt__marker-row">
      <div class="prt__marker-spacer" :style="labelColumnStyle" />
      <div class="prt__marker-track" :style="trackStyle">
        <p v-if="mergedMarkerLabel" class="prt__marker-label prt__marker-label--handoff" :style="mergedMarkerStyle">
          {{ mergedMarkerLabel }}
        </p>
        <template v-else>
          <p
            v-if="todayMarkerLabel"
            class="prt__marker-label"
            :class="{ 'prt__marker-label--handoff': false }"
            :style="todayMarkerStyle"
          >
            {{ todayMarkerLabel }}
          </p>
          <p
            v-if="handoffMarkerLabel"
            class="prt__marker-label prt__marker-label--handoff"
            :style="handoffMarkerStyle"
          >
            {{ handoffMarkerLabel }}
          </p>
        </template>
      </div>
    </div>

    <div class="prt__body">
      <div
        v-if="showTodayLine"
        class="prt__today-line"
        :style="{ left: `${todayLineLeftPx}px` }"
        aria-hidden="true"
      />
      <div
        v-for="marker in handoffLines"
        :key="marker.key"
        class="prt__handoff-line"
        :style="{ left: `${marker.leftPx}px` }"
        aria-hidden="true"
      />
      <div
        v-for="marker in retiredMarkers"
        :key="marker.key"
        class="prt__retired-cap"
        :style="{ left: `${marker.leftPx}px` }"
      >
        <span class="prt__retired-cap-label">Retired</span>
      </div>

      <div class="prt__lane">
        <div class="prt__label" :style="labelColumnStyle">
          <p class="prt__label-main">Pay range / versions</p>
        </div>
        <div class="prt__track" :style="trackStyle">
          <template v-if="laneError">
            <div class="prt__error">
              <span class="prt__error-text">Couldn't load pay range versions</span>
              <button type="button" class="prt__retry" @click="emit('retry')">Retry</button>
            </div>
          </template>
          <template v-else-if="loading">
            <div class="prt__skeleton-row">
              <span
                v-for="(w, i) in skeletonWidths"
                :key="`sk-${i}`"
                class="prt__skeleton"
                :style="{ width: `${w}px` }"
              />
            </div>
          </template>
          <template v-else-if="!hasVersions">
            <div class="prt__empty">No pay range versions</div>
          </template>
          <template v-else>
            <div class="prt__rows" :style="{ minHeight: `${laneContentHeight}px` }">
              <PayRangeVersionTimelineBarRow
                v-for="row in versionRows"
                :key="rowStableKey(row)"
                :segments="row"
                :selected-version="selectedVersion"
                @select="onBarSelect"
                @bar-hover="onBarHover"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="prt__axis">
      <div class="prt__axis-tier">
        <div class="prt__axis-spacer" :style="labelColumnStyle" />
        <div class="prt__axis-track" :style="trackStyle">
          <div
            v-for="unit in yearAxisUnits"
            :key="`y-${unit.startMs}`"
            class="prt__axis-cell prt__axis-cell--year"
            :class="{ 'prt__axis-cell--today': unit.isTodayUnit }"
            :style="{ width: `${unit.widthPx}px` }"
          >
            <span class="prt__axis-label">{{ unit.label }}</span>
            <span v-if="unit.endLabel" class="prt__axis-label prt__axis-label--end">{{
              unit.endLabel
            }}</span>
          </div>
        </div>
      </div>
      <div v-if="subAxisUnits.length" class="prt__axis-tier">
        <div class="prt__axis-spacer" :style="labelColumnStyle" />
        <div class="prt__axis-track" :style="trackStyle">
          <div
            v-for="unit in subAxisUnits"
            :key="`s-${unit.startMs}`"
            class="prt__axis-cell prt__axis-cell--sub"
            :class="{ 'prt__axis-cell--today': unit.isTodayUnit }"
            :style="{ width: `${unit.widthPx}px` }"
          >
            <span class="prt__axis-label">{{ unit.label }}</span>
          </div>
        </div>
      </div>
    </div>
    </div>

    <footer v-if="!compact && !loading && visibleLegend.length" class="prt__legend">
      <span class="prt__legend-heading">Legend</span>
      <div v-for="item in visibleLegend" :key="item.swatch" class="prt__legend-item">
        <span class="prt__swatch" :class="`prt__swatch--${item.swatch}`" aria-hidden="true" />
        <span class="prt__legend-text">{{ item.label }}</span>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PayRangeVersionTimelineBarRow from "./PayRangeVersionTimelineBarRow.vue";
import {
  buildVersionLayoutRows,
  LEGEND_ITEMS,
  normalizeVersionNumber,
  normalizeVersionRow,
} from "./payRangeVersionTimelineUtils";
import {
  SCALE_OPTIONS,
  SCALE_CONFIG,
  buildAxisUnits,
  defaultWindowStartMs,
  dateToX,
  isTodayInWindow,
  normalizeScale,
  shiftWindowStart,
  todayLineX,
  trackWidthForScale,
  unitContainingToday,
  windowEndMs,
  LABEL_COLUMN_PX,
  COMPACT_LABEL_COLUMN_PX,
} from "@components/Timeline/employeeTimelineGeometry";
import {
  formatAxisMonth,
  formatAxisQuarter,
  formatAxisYear,
  toIso,
  toTime,
} from "@components/Timeline/timelineDateUtils";

const MARKER_COLLIDE_PX = 48;

const props = defineProps({
  versions: { type: Array, default: () => [] },
  today: { type: [String, Date], default: () => new Date() },
  handoffDate: { type: [String, Date], default: null },
  handoffLabel: { type: String, default: "hand-off" },
  currencySymbol: { type: String, default: "$" },
  defaultZoom: { type: String, default: "" },
  selectedVersion: { type: [Number, String], default: null },
  hoveredVersion: { type: [Number, String], default: null },
  unitSuffix: { type: String, default: "USD/hour" },
  compact: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  laneError: { type: Boolean, default: false },
  initialWindowStart: { type: String, default: "" },
});

const emit = defineEmits(["select", "bar-select", "bar-hover", "retry"]);

const skeletonWidths = [160, 110, 200, 90];

const scale = ref(normalizeScale(props.defaultZoom || "years"));

function resolveWindowStart() {
  if (props.initialWindowStart) {
    const t = toTime(props.initialWindowStart);
    if (t != null) return t;
  }
  return defaultWindowStartMs(toTime(props.today), scale.value);
}

const windowStartMs = ref(resolveWindowStart());

watch(
  () => props.defaultZoom,
  (val) => {
    if (val) scale.value = normalizeScale(val);
  }
);

watch(
  () => [props.today, scale.value, props.initialWindowStart],
  () => {
    windowStartMs.value = resolveWindowStart();
  }
);

const todayMs = computed(() => toTime(props.today) ?? Date.now());

const normalizedVersions = computed(() => (props.versions || []).map(normalizeVersionRow));

const handoffMs = computed(() => {
  if (props.handoffDate) return toTime(props.handoffDate);
  const draft = normalizedVersions.value.find((v) => v.state === "draft");
  return toTime(draft?.effectiveFrom ?? null);
});

const hasVersions = computed(() => normalizedVersions.value.length > 0);

const scrollEl = ref(null);
const containerWidthPx = ref(0);
let resizeObserver = null;

/** Measure the scroll container's client width; jsdom reports 0, so tests mock it. */
function measureContainer() {
  const el = scrollEl.value;
  if (!el) return;
  containerWidthPx.value = Math.max(0, el.clientWidth || 0);
}

onMounted(() => {
  measureContainer();
  if (typeof ResizeObserver !== "undefined" && scrollEl.value) {
    resizeObserver = new ResizeObserver(measureContainer);
    resizeObserver.observe(scrollEl.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

const labelColumnPx = computed(() =>
  props.compact ? COMPACT_LABEL_COLUMN_PX : LABEL_COLUMN_PX
);

/**
 * Fill the card on wide screens: track width = available container width minus
 * the label column. On narrow cards keep the base fixed width (per scale) so
 * the timeline scrolls horizontally instead of compressing below its minimum.
 */
const trackWidthPx = computed(() => {
  const minWidth = trackWidthForScale(scale.value, props.compact);
  const available = containerWidthPx.value - labelColumnPx.value;
  return Math.max(available, minWidth);
});

/** Axis unit width derived from the responsive track width (replaces fixed px). */
const scaleUnitWidthPx = computed(() => {
  const cfg = SCALE_CONFIG[scale.value];
  return trackWidthPx.value / cfg.unitCount;
});

const labelColumnStyle = computed(() => ({
  width: `${labelColumnPx.value}px`,
  minWidth: `${labelColumnPx.value}px`,
}));

const trackStyle = computed(() => ({
  width: `${trackWidthPx.value}px`,
  minWidth: `${trackWidthPx.value}px`,
}));

const windowEndMsValue = computed(() => windowEndMs(windowStartMs.value, scale.value));

function setScale(next) {
  scale.value = normalizeScale(next);
  windowStartMs.value = defaultWindowStartMs(todayMs.value, scale.value);
}

function shiftWindow(direction) {
  windowStartMs.value = shiftWindowStart(
    windowStartMs.value,
    scale.value,
    direction === "forward" ? "forward" : "back"
  );
}

function recenterToday() {
  windowStartMs.value = defaultWindowStartMs(todayMs.value, scale.value);
}

const todayInsideWindow = computed(() =>
  isTodayInWindow(todayMs.value, windowStartMs.value, windowEndMsValue.value)
);

const showTodayLine = computed(() => !props.loading && todayInsideWindow.value);

const todayLineLeftPx = computed(() =>
  labelColumnPx.value +
  todayLineX(todayMs.value, windowStartMs.value, windowEndMsValue.value, trackWidthPx.value)
);

function markerX(timeMs) {
  if (timeMs == null) return null;
  return (
    labelColumnPx.value +
    dateToX(timeMs, windowStartMs.value, windowEndMsValue.value, trackWidthPx.value)
  );
}

const todayMarkerLabel = computed(() => {
  if (mergedMarkerLabel.value) return "";
  if (todayInsideWindow.value) return "today";
  const x = todayLineX(todayMs.value, windowStartMs.value, windowEndMsValue.value, trackWidthPx.value);
  if (x <= 0) return "‹ today";
  if (x >= trackWidthPx.value) return "today ›";
  return "today";
});

const todayMarkerStyle = computed(() => styleForMarker(todayMs.value, todayMarkerLabel.value));

const handoffInsideWindow = computed(() => {
  const h = handoffMs.value;
  if (h == null) return false;
  return h >= windowStartMs.value && h < windowEndMsValue.value;
});

const handoffMarkerLabel = computed(() => {
  if (mergedMarkerLabel.value || !handoffMs.value) return "";
  if (!handoffInsideWindow.value) return "";
  return props.handoffLabel;
});

const handoffMarkerStyle = computed(() => styleForMarker(handoffMs.value, handoffMarkerLabel.value));

const todayIso = computed(() => toIso(props.today));
const handoffIso = computed(() => toIso(handoffMs.value));

const mergedMarkerLabel = computed(() => {
  if (!todayIso.value || !handoffIso.value || todayIso.value !== handoffIso.value) return "";
  if (!handoffInsideWindow.value && !todayInsideWindow.value) return "";
  return `today · ${props.handoffLabel}`;
});

const mergedMarkerStyle = computed(() => styleForMarker(handoffMs.value, mergedMarkerLabel.value));

function styleForMarker(timeMs, label) {
  if (!label || timeMs == null) return {};
  const trackLeft = labelColumnPx.value;
  const x = dateToX(timeMs, windowStartMs.value, windowEndMsValue.value, trackWidthPx.value);
  if (!todayInsideWindow.value && x <= 0) return { left: `${trackLeft}px` };
  if (!todayInsideWindow.value && x >= trackWidthPx.value) {
    return { left: `${trackLeft + trackWidthPx.value}px`, transform: "translateX(-100%)" };
  }
  return { left: `${trackLeft + x}px`, transform: "translateX(-50%)" };
}

const markersClose = computed(() => {
  const t = markerX(todayMs.value);
  const h = markerX(handoffMs.value);
  if (t == null || h == null || mergedMarkerLabel.value) return false;
  return Math.abs(t - h) < MARKER_COLLIDE_PX;
});

const handoffLines = computed(() => {
  if (mergedMarkerLabel.value || !handoffInsideWindow.value) return [];
  return [{ key: "handoff", leftPx: markerX(handoffMs.value) }];
});

const todayUnit = computed(() =>
  unitContainingToday(windowStartMs.value, todayMs.value, scale.value)
);

function buildYearAxisUnits() {
  const z = scale.value;
  if (z === "years") {
    return buildAxisUnits(windowStartMs.value, z).map((unit, index, arr) => ({
      ...unit,
      widthPx: scaleUnitWidthPx.value,
      label: formatAxisYear(unit.startMs),
      endLabel:
        index === arr.length - 1
          ? String(new Date(addYearEnd(unit.startMs)).getUTCFullYear())
          : "",
      isTodayUnit: todayUnit.value?.startMs === unit.startMs,
    }));
  }
  const yearStarts = new Map();
  buildAxisUnits(windowStartMs.value, z).forEach((unit) => {
    const y = formatAxisYear(unit.startMs);
    if (!yearStarts.has(y)) {
      yearStarts.set(y, {
        startMs: unit.startMs,
        widthPx: 0,
        label: y,
        endLabel: "",
        isTodayUnit: false,
      });
    }
    const entry = yearStarts.get(y);
    entry.widthPx += scaleUnitWidthPx.value;
    if (todayUnit.value && unit.startMs === todayUnit.value.startMs) {
      entry.isTodayUnit = true;
    }
  });
  return [...yearStarts.values()];
}

function addYearEnd(ms) {
  return Date.UTC(new Date(ms).getUTCFullYear() + 1, 0, 1);
}

const yearAxisUnits = computed(() => buildYearAxisUnits());

const subAxisUnits = computed(() => {
  const z = scale.value;
  if (z === "years") return [];
  return buildAxisUnits(windowStartMs.value, z).map((unit) => ({
    ...unit,
    widthPx: scaleUnitWidthPx.value,
    label: z === "quarters" ? formatAxisQuarter(unit.startMs) : formatAxisMonth(unit.startMs),
    isTodayUnit: todayUnit.value?.startMs === unit.startMs,
  }));
});

const versionRows = computed(() =>
  buildVersionLayoutRows(normalizedVersions.value, {
    currencySymbol: props.currencySymbol,
    todayMs: todayMs.value,
    handoffMs: handoffMs.value,
    windowStartMs: windowStartMs.value,
    windowEndMs: windowEndMsValue.value,
    trackWidthPx: trackWidthPx.value,
  })
);

const laneContentHeight = computed(() => {
  const rows = versionRows.value.length || 1;
  return rows * 24 + Math.max(0, rows - 1) * 6;
});

const retiredMarkers = computed(() => {
  if (props.loading || props.laneError) return [];
  const markers = [];
  versionRows.value.flat().forEach((seg) => {
    if (seg.type !== "bar" || !seg.record?.meta?.retired) return;
    markers.push({
      key: `ret-${seg.record.id}`,
      leftPx: seg.leftPx + seg.widthPx,
    });
  });
  return markers;
});

function rowStableKey(row) {
  if (!row?.length) return "row-empty";
  return row.map((s) => s.key).join("_");
}

const visibleLegend = computed(() => {
  if (!hasVersions.value) return [];
  const present = new Set(normalizedVersions.value.map((v) => v.state));
  const items = LEGEND_ITEMS.filter((item) => present.has(item.versionState));
  if (normalizedVersions.value.some((v) => v.retired)) {
    items.push({ swatch: "retired", label: "Retired", versionState: "retired" });
  }
  return items;
});

const ariaLabel = computed(() => {
  const parts = normalizedVersions.value.map((v) => `V${v.version} (${v.state})`);
  return `Pay range version timeline: ${parts.join("; ") || "empty"}`;
});

function onBarSelect(version) {
  const v = normalizeVersionNumber(version);
  emit("select", v);
  emit("bar-select", v);
}

function onBarHover(version) {
  emit("bar-hover", normalizeVersionNumber(version));
}
</script>

<style scoped>
.prt {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  font-family: Inter, sans-serif;
  width: 100%;
  min-width: 0;
}

.prt__scroll {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
}

.prt__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.prt__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #101828;
}

.prt__controls {
  display: flex;
  flex-shrink: 0;
  gap: 12px;
}

.prt__btn-group {
  display: flex;
  overflow: hidden;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.prt__btn {
  min-height: 40px;
  padding: 8px 16px;
  border: none;
  border-right: 1px solid #d0d5dd;
  background: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}

.prt__btn:last-child {
  border-right: none;
}

.prt__btn--active {
  background: #f9fafb;
}

.prt__marker-row {
  display: flex;
}

.prt__marker-spacer,
.prt__axis-spacer {
  flex-shrink: 0;
}

.prt__marker-track {
  position: relative;
  height: 13px;
}

.prt__marker-label {
  position: absolute;
  top: 0;
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: #495569;
  white-space: nowrap;
}

.prt__marker-label--handoff {
  color: #7f56d9;
}

.prt__body {
  position: relative;
  border-top: 1px solid #eaecf0;
}

.prt__today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  width: 1px;
  background: #495569;
  pointer-events: none;
}

.prt__handoff-line {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  width: 1.5px;
  background: #7f56d9;
  pointer-events: none;
}

.prt__retired-cap {
  position: absolute;
  top: 12px;
  z-index: 3;
  width: 0;
  pointer-events: none;
}

.prt__retired-cap-label {
  position: absolute;
  left: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #b54708;
  white-space: nowrap;
}

.prt__lane {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
}

.prt__label {
  box-sizing: border-box;
  flex-shrink: 0;
  padding-right: 16px;
}

.prt__label-main {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}

.prt__track {
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.prt__rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.prt__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  border: 1px dashed #d0d5dd;
  border-radius: 4px;
  font-size: 11px;
  color: #98a2b3;
}

.prt__error {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 24px;
  border: 1px dashed #fda29b;
  border-radius: 4px;
  background: #fef3f2;
  font-size: 11px;
  color: #b42318;
}

.prt__retry {
  padding: 0;
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  color: #b42318;
  text-decoration: underline;
  cursor: pointer;
}

.prt__skeleton-row {
  display: flex;
  gap: 8px;
  height: 24px;
}

.prt__skeleton {
  height: 24px;
  border-radius: 4px;
  background: #eaecf0;
}

.prt__axis-tier {
  display: flex;
}

.prt__axis-track {
  display: flex;
}

.prt__axis-cell {
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  height: 20px;
  padding: 6px 4px 0;
  border-top: 1px solid #d0d5dd;
  border-left: 1px solid #d0d5dd;
}

.prt__axis-cell--year:last-child,
.prt__axis-cell--sub:last-child {
  border-right: 1px solid #d0d5dd;
}

.prt__axis-cell--today {
  background: #f9fafb;
}

.prt__axis-label {
  font-size: 11px;
  font-weight: 400;
  color: #667085;
}

.prt__axis-cell--today .prt__axis-label {
  font-weight: 500;
  color: #344054;
}

.prt__legend {
  display: flex;
  gap: 16px;
  align-items: center;
}

.prt__legend-heading {
  width: 184px;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}

.prt__legend-item {
  display: flex;
  gap: 6px;
  align-items: center;
}

.prt__legend-text {
  font-size: 12px;
  color: #667085;
}

.prt__swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.prt__swatch--draft {
  background: var(--component-colors-utility-fuchsia-utility-fuchsia-50, #fdf4ff);
  border: 1px dashed var(--component-colors-utility-fuchsia-utility-fuchsia-500, #d444f1);
}

.prt__swatch--current {
  background: #dcfae6;
  border: 1px solid #75e0a7;
}

.prt__swatch--lapsed {
  background: #f2f4f7;
  border: 1px solid #d0d5dd;
}

.prt__swatch--future {
  background: #eff8ff;
  border: 1px solid #b2ddff;
}

.prt__swatch--retired {
  background: #ffffff;
  border: 1px solid #fedf89;
  box-shadow: inset 3px 0 0 #f79009;
}

.prt--compact {
  gap: 0;
  padding: 12px;
}
</style>
